'use client';

import { fetchDynamicSectionsById } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, boolean as __eliadmin_zod_2 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { GDPRTextContactForm } from '@eliadmin/core/src/utils/gdpr-tos/gdpr-text-contact-form';
import { Field } from '@eliadmin/core/src/components/form/field';
import type { TDynamicData } from '../../types/dynamic-data';
import { getContactCopy } from './contact-copy';

const contactSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    custom: __eliadmin_zod_0({
      company: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      reason: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    }),
    email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    text: __eliadmin_zod_1(),
    gdpr: __eliadmin_zod_2().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': __eliadmin_zod_1().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function ContactForm() {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      custom: {
        company: '',
        reason: '',
      },
      phone: '',
      email: '',
      text: '',
      gdpr: false,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const copy = getContactCopy(locale);
  const router = useRouter();

  const { data: reasons = [] } = useQuery({
    queryKey: ['REASONS', locale],
    queryFn: () => fetchDynamicSectionsById<TDynamicData>({ locale, id: 4 }),
  });

  const handleSubmit = form.handleSubmit(async data => {
    try {
      await mutateAsync({
        data: {
          ...data,
        },
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className={cn('grid w-full grid-cols-12 gap-4 @container', 'ea-contact-form')}
      >
        <Field
          control={form.control}
          name={'name'}
          label={copy.name}
          required
          placeholder={copy.name}
          className={{ wrapper: cn('col-span-12 sm:col-span-6', 'ea-contact-form__field') }}
        />
        <Field
          control={form.control}
          name={'custom.company'}
          label={t('text1432')}
          required
          placeholder={t('text1432')}
          className={{ wrapper: cn('col-span-12 sm:col-span-6', 'ea-contact-form__field') }}
        />
        <Field
          control={form.control}
          type="email"
          name={'email'}
          label="E-mail"
          required
          placeholder="E-mail"
          className={{ wrapper: cn('col-span-12 sm:col-span-6', 'ea-contact-form__field') }}
        />
        <Field
          control={form.control}
          name={'phone'}
          label={t('text1258')}
          placeholder={t('text1258')}
          className={{ wrapper: cn('col-span-12 sm:col-span-6', 'ea-contact-form__field') }}
        />
        <Field
          control={form.control}
          name={'custom.reason'}
          label={t('text3261')}
          required
          placeholder="Vyberte"
          type="select"
          options={reasons.map(r => ({ label: r.name, value: r.name }))}
          className={{ wrapper: cn('col-span-12 ', 'ea-contact-form__field') }}
        />

        <FormField
          control={form.control}
          name={'text'}
          render={({ field }) => (
            <FormItem
              required
              className={cn('col-span-12', 'ea-contact-form__field ea-contact-form__text-field')}
            >
              <FormLabel>
                <span>{t('text80')}</span>
              </FormLabel>

              <div
                className={cn(
                  'flex w-full flex-row items-center space-x-2',
                  'ea-contact-form__input-wrapper ea-contact-form__text-input-wrapper',
                )}
              >
                <FormControl>
                  <Textarea
                    placeholder={t('text2340')}
                    {...(field as any)}
                    rows={5}
                    rounded={CONTACT_CONFIG.form.input.rounded}
                  />
                </FormControl>
              </div>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="gdpr"
          render={({ field }) => (
            <FormItem
              className={cn(
                'col-span-12 flex items-start',
                'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
              )}
            >
              <FormControl>
                <Checkbox
                  ref={field.ref}
                  name={field.name}
                  disabled={field.disabled}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                />
              </FormControl>
              <FormLabel className={cn('pt-[3px]', 'ea-contact-form__gdpr-checkbox-label')}>
                <GDPRTextContactForm locale={locale} />
              </FormLabel>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'g-recaptcha-response'}
          render={() => (
            <FormItem required className={cn('col-span-12')}>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="col-span-12 flex w-full items-center justify-center">
          <Button
            type="submit"
            variant={CONTACT_CONFIG.form.submit.variant}
            className={cn('w-full sm:w-fit', 'ea-contact-form__submit')}
            isLoading={form.formState.isSubmitting}
            aria-label={t('text185')}
          >
            {t('text185')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
